import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { Button, Calendar, MultiSelect, RadioButton, TOAST_TYPE } from '@base'
import { ButtonsWrapper, FormItemWrapper, FormWrapper, InfoWrapper } from '../LogisticCalculator/styles'
import { showLoader, showToast } from '@data/state/action'
import { getAllUsers, getBillingAddressesApi, moveOrderFilesWithinS3 } from '@data'
import { MBOOK_ORGANISATION_ID, strings } from '@constants'
import { displayName } from '@utils'
import { AdminWrapper } from '../TCEndorsement/styles'
import { Label, RadioButtonWrapper } from '@pages/Admin/DispatchPlan/style'
import { Spacings } from '@styles'

const CustomFormWrapper = styled(FormWrapper)`
	flex-grow: 1;
`

const RadioButtonsWrapper = styled.div`
	display: flex;
	flex-direction: row;
	margin: ${Spacings.SPACING_2B} ${Spacings.SPACING_1B};
`

const deriveFormState = () => {
	const state = {
		organisation_ids: {
			id: 'organisation-ids',
			label: strings('organisation'),
			placeholder: strings(['select', 'organisation']),
			value: [],
			displayKey: 'display_name',
			primaryKey: 'organisation_id',
			required: false,
			disabled: false,
		},
		from_date: {
			id: 'from-date',
			label: strings('from'),
			value: null,
			disabled: false,
			required: false,
		},
		to_date: {
			id: 'to-date',
			label: strings('to'),
			value: null,
			disabled: false,
			required: false,
		},
		mbook_gsts: {
			id: 'mbook-gsts',
			label: strings('mbook', 'gst'),
			placeholder: strings(['select', 'mbook', 'gst']),
			value: [],
			primaryKey: 'gst',
			displayKey: 'display_name',
			disabled: false,
			required: false,
		},
		buyer_organisation: {
			id: 'buyer-organisation',
			label: strings('buyer', 'organisation'),
			value: 'TRUE',
		},
	}
	return state
}

const PoInvoiceMapping = () => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState)
	const [organisationList, setOrganisationList] = useState([])
	const [mbookGsts, setMbookGsts] = useState([])

	useEffect(() => {
		dispatch(showLoader(true))
		const getOrgPromise = getAllUsers()

		const getMbookGstPromise = getBillingAddressesApi(MBOOK_ORGANISATION_ID)
		Promise.all([getOrgPromise, getMbookGstPromise])
			.then((values) => {
				setOrganisationList(values[0]?.map((item) => ({ ...item, display_name: displayName(item) })) ?? [])
				setMbookGsts(values[1]?.map((item) => ({ ...item, display_name: `${item?.gst} | ${item?.address?.state}` })) ?? [])
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	const resetForm = () => {
		setFormState(deriveFormState())
	}
	const submitHandler = () => {
		const fromDate = new Date(formState?.from_date?.value ?? Date.now())
		const toDate = new Date(formState?.to_date?.value ?? Date.now())
		const oneYearAfterFrom = new Date(fromDate)
		oneYearAfterFrom.setFullYear(oneYearAfterFrom.getFullYear() + 1)
		if (toDate > oneYearAfterFrom) {
			dispatch(showToast(true, strings('msg_error_max_one_year'), { type: TOAST_TYPE.ERROR }))
			return
		}
		dispatch(showLoader(true))
		const obj = {
			organisation_ids: formState?.organisation_ids?.value?.reduce((acc, item) => {
				acc.push(item?.organisation_id)
				return acc
			}, []),
			mbook_gsts: formState?.mbook_gsts?.value?.reduce((acc, item) => {
				acc.push(item?.gst)
				return acc
			}, []),
			buyer_organisation: formState?.buyer_organisation?.value === 'TRUE',
		}
		moveOrderFilesWithinS3(formState?.from_date?.value, formState?.to_date?.value, obj)
			.then((response) => {
				const message = response?.message ?? strings('msg_order_files_moved_successfully')
				dispatch(showToast(true, message, { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				const error_msg = error?.response?.data?.message ?? strings('msg_order_files_mnoved_failure')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				resetForm()
				dispatch(showLoader(false))
			})
	}

	const isDisabled = useMemo(() => {
		return !formState?.from_date?.value || !formState?.to_date?.value
	}, [formState?.from_date?.value, formState?.to_date?.value])
	return (
		<AdminWrapper>
			<CustomFormWrapper>
				<FormItemWrapper>
					<InfoWrapper>
						<MultiSelect
							label={formState?.organisation_ids?.label}
							id={formState?.organisation_ids?.id}
							placeholder={formState?.organisation_ids?.placeholder}
							value={formState?.organisation_ids?.value ?? []}
							onChange={updateState('organisation_ids')}
							data={organisationList}
							displayKey={formState?.organisation_ids?.displayKey}
							primaryKey={formState?.organisation_ids?.primaryKey}
							required={formState?.organisation_ids?.required}
							disabled={formState?.organisation_ids?.disabled}
							multiple
							showAllOptions
						/>
					</InfoWrapper>
					<InfoWrapper>
						<Calendar
							id={formState?.from_date?.id}
							label={formState?.from_date?.label}
							value={formState?.from_date?.value ?? ''}
							required={formState?.from_date?.required}
							disabled={formState?.from_date?.disabled}
							onChange={updateState('from_date')}
						/>
					</InfoWrapper>
					<InfoWrapper>
						<Calendar
							id={formState?.to_date?.id}
							label={formState?.to_date?.label}
							value={formState?.to_date?.value ?? ''}
							required={formState?.to_date?.required}
							disabled={formState?.to_date?.disabled}
							onChange={updateState('to_date')}
						/>
					</InfoWrapper>
					<InfoWrapper>
						<MultiSelect
							label={formState?.mbook_gsts?.label}
							id={formState?.mbook_gsts?.id}
							placeholder={formState?.mbook_gsts?.placeholder}
							value={formState?.mbook_gsts?.value ?? []}
							onChange={updateState('mbook_gsts')}
							data={mbookGsts ?? []}
							displayKey={formState?.mbook_gsts?.displayKey}
							primaryKey={formState?.mbook_gsts?.primaryKey}
							required={formState?.mbook_gsts?.required}
							disabled={formState?.mbook_gsts?.disabled}
							multiple
							showAllOptions
						/>
					</InfoWrapper>
					<RadioButtonsWrapper>
						<RadioButtonWrapper>
							<RadioButton
								id='buyer-organisation-radio-yes'
								size='1.125'
								checked={formState?.buyer_organisation?.value === 'TRUE'}
								value={'TRUE'}
								onChange={() => {
									updateState('buyer_organisation')('TRUE')
								}}
							/>
							<Label>{strings('buyer')}</Label>
						</RadioButtonWrapper>
						<RadioButtonWrapper>
							<RadioButton
								id='buyer-organisation-radio-no'
								size='1.125'
								checked={formState?.buyer_organisation?.value === 'FALSE'}
								value={'FALSE'}
								onChange={() => {
									updateState('buyer_organisation')('FALSE')
								}}
							/>
							<Label>{strings('supplier')}</Label>
						</RadioButtonWrapper>
					</RadioButtonsWrapper>
					<ButtonsWrapper>
						<Button type='primary' variant='destructive' small onClick={resetForm}>
							{strings('reset')}
						</Button>
						<Button type='primary' small disabled={isDisabled} onClick={submitHandler}>
							{strings('submit')}
						</Button>
					</ButtonsWrapper>
				</FormItemWrapper>
			</CustomFormWrapper>
		</AdminWrapper>
	)
}

export default PoInvoiceMapping
